import { css } from "@emotion/css";
import { fontFamily, fontSize, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactElement } from "react";
import React, { useMemo } from "react";
import type { HunkData } from "react-diff-view";
import { Hunk, Diff, markEdits, parseDiff, tokenize, useMinCollapsedLines, useSourceExpansion } from "react-diff-view";
import "react-diff-view/style/index.css";
import UnfoldCollapsed from "~/components/DiffViewer/UnfoldCollapsed";
import { useOctopusTheme } from "~/components/Theme/index";
export type DiffViewType = "split" | "unified";
type DiffViewerProps = {
    originalText: string;
    updatedText: string;
    unifiedDiff: string;
    diffType: DiffViewType;
};
export const DiffViewer = (props: DiffViewerProps) => {
    const theme = useOctopusTheme();
    const styles = {
        diffStyle: css({
            fontSize: fontSize["medium"],
            "--diff-code-delete-text-color": theme.diffRemovedColor,
            "--diff-code-delete-background-color": theme.diffRemovedBackground,
            "--diff-code-delete-edit-background-color": theme.diffWordRemovedBackground,
            "--diff-code-delete-edit-text-color": theme.diffWordRemovedColor,
            "--diff-gutter-delete-background-color": theme.diffRemovedGutterBackground,
            "--diff-gutter-delete-text-color": theme.diffRemovedGutterColor,
            "--diff-code-insert-text-color": theme.diffAddedColor,
            "--diff-code-insert-background-color": theme.diffAddedBackground,
            "--diff-code-insert-edit-background-color": theme.diffWordAddedBackground,
            "--diff-code-insert-edit-text-color": theme.diffWordAddedColor,
            "--diff-gutter-insert-background-color": theme.diffAddedGutterBackground,
            "--diff-gutter-insert-text-color": theme.diffAddedGutterColor,
            "--diff-text-color": themeTokens.color.text.primary,
            "--diff-font-family": fontFamily.code,
            "--diff-omit-gutter-line-color": "transparent",
        }),
    };
    const { type, hunks } = useMemo(() => parseDiff(props.unifiedDiff)[0], [props.unifiedDiff]);
    const [hunksWithSourceExpanded, expandRange] = useSourceExpansion(hunks, props.originalText);
    const hunksWithMinLinesCollapsed = useMinCollapsedLines(2, hunksWithSourceExpanded, props.originalText);
    const tokens = useMemo(() => tokenize(hunksWithMinLinesCollapsed, {
        highlight: false,
        enhancers: [markEdits(hunksWithMinLinesCollapsed, { type: "block" })],
    }), [hunksWithMinLinesCollapsed]);
    const linesCount = props.originalText ? props.originalText.split("\n").length : 0;
    const renderHunk = (children: ReactElement[], hunk: HunkData, i: number, hunks: HunkData[]) => {
        const previousElement = children[children.length - 1];
        const decorationElement = <UnfoldCollapsed key={`decoration-${hunk.content}`} previousHunk={previousElement && previousElement.props.hunk} currentHunk={hunk} linesCount={linesCount} onExpand={expandRange}/>;
        children.push(decorationElement);
        const hunkElement = <Hunk key={`hunk-${hunk.content}`} hunk={hunk}/>;
        children.push(hunkElement);
        if (i === hunks.length - 1 && props.originalText) {
            const unfoldTailElement = <UnfoldCollapsed key="decoration-tail" previousHunk={hunk} linesCount={linesCount} onExpand={expandRange}/>;
            children.push(unfoldTailElement);
        }
        return children;
    };
    return (<div className={styles.diffStyle}>
            <Diff viewType={props.diffType} diffType={type} hunks={hunksWithMinLinesCollapsed} tokens={tokens}>
                {(hunks) => hunks.reduce(renderHunk, [])}
            </Diff>
        </div>);
};
